import { useState } from 'react'

import { Accordion, Button as MantineButton } from '@mantine/core'
import dayjs from 'dayjs'
import { v1 as uuid } from 'uuid'

import ApplicationAccordionItem from '../components/ApplicationAccordionItem'
import ApplicationAddressInput from '../components/ApplicationAddressInput'
import ApplicationRadioQuestion from '../components/ApplicationRadioQuestion'
import ApplicationScreenWrapper from '../components/ApplicationScreenWrapper'
import ApplicationTextInput from '../components/ApplicationTextInput'
import { useFormLeaveGuard } from '../hooks/useLeaveGuard'
import { formatMonthString, formatMonthYear } from '../utils/formatters'
import { useFormContext } from '../utils/FormContext'
import { buildAccidentsPayload } from '../utils/stepPayloads'
import { findErrors } from '../utils/validators'

const defaultAccident = {
  city: undefined,
  state: undefined,
  country: undefined,
  description: undefined,
  isPreventable: false,
  isCommercialMotorVehicleInvolved: false,
  involvedFatalities: false,
  involvedInjuries: false,
  accidentDate: undefined,
}

export const MainAccidentsScreen = () => {
  const [accordionValue, setAccordionValue] = useState(null)
  const [controlValue, setControlValue] = useState('false')

  const { form, handleNext, updateLoading, application, backText } =
    useFormContext()
  const { goBack, leaveGuardModal } = useFormLeaveGuard(
    buildAccidentsPayload
  )

  const getAccidentTitle = (accident, index) => {
    if (
      accident.accidentDate !== undefined &&
      dayjs(accident.accidentDate).utc().format('MMM YYYY') !== 'Invalid Date'
    ) {
      return `Event in ${dayjs(accident.accidentDate).utc().format('MMM YYYY')}`
    } else {
      return `Event ${index + 1}`
    }
  }

  return (
    <ApplicationScreenWrapper
      title={'Accidents & Violations'}
      description="Please list all motor vehicle accidents and traffic violations you were involved in over the past 3 years."
      goBack={goBack}
      application={application}
      updateLoading={updateLoading}
      handleCancel={goBack}
      backText={backText}
      saveButtontext={'Save and continue'}
      handleSubmit={() => {
        setAccordionValue(null)
        handleNext(buildAccidentsPayload(form.values))
      }}
    >
      {leaveGuardModal}
      <div
        className={`${form.values.accidents.length > 0 ? 'hidden' : ''} w-full`}
      >
        <ApplicationRadioQuestion
          question="Have you had any accidents or violations?"
          value={controlValue}
          handleChange={(e) => {
            setControlValue(e)
            if (e === 'false') {
              form.setFieldValue('accidents', [])
            } else {
              setAccordionValue(`accident-${form.values.accidents.length + 1}`)
              const key = uuid()
              const accident = {
                ...defaultAccident,
                key,
              }
              form.setFieldValue('accidents', [
                ...form.values.accidents,
                accident,
              ])
            }
          }}
        />
      </div>
      <Accordion
        chevronPosition="left"
        variant="separated"
        value={accordionValue}
        onChange={setAccordionValue}
        radius={'md'}
        classNames={{
          root: 'w-full sm:col-span-3',
          control: 'h-16',
          item: 'bg-transparent border-doubleNickel-gray-300 shadow-sm',
        }}
      >
        {form.values.accidents.map((accident, index) => (
          <ApplicationAccordionItem
            value={`accident-${index + 1}`}
            key={accident.key}
            title={getAccidentTitle(form.values.accidents[index], index)}
            hasErrors={findErrors(form.errors, 'accidents', index) > 0}
            isCurrent={false}
            handleDelete={() => form.removeListItem('accidents', index)}
          >
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-8 sm:gap-4">
              <ApplicationTextInput
                label="Event Date"
                placeholder={'MM / YYYY'}
                required
                pattern="\d*"
                value={formatMonthString(
                  form.values.accidents[index].accidentDate
                )}
                error={form.errors[`accidents.${index}.accidentDate`]}
                onChange={(event) => {
                  formatMonthYear(
                    `accidents.${index}.accidentDate`,
                    event,
                    form
                  )
                }}
              />

              <ApplicationAddressInput
                label="Event Location"
                placeholder="City, State, Country"
                required
                value={form.values.accidents[index]}
                error={
                  form.errors[`accidents.${index}.city`] ||
                  form.errors[`accidents.${index}.state`]
                }
                types={['(cities)']}
                handleChange={(value) => {
                  form.setFieldValue(`accidents.${index}.city`, value.city)
                  form.setFieldValue(`accidents.${index}.state`, value.state)
                  form.setFieldValue(
                    `accidents.${index}.country`,
                    value.country
                  )
                }}
              />

              <ApplicationTextInput
                label="Event Description"
                placeholder="Example: Rear-ended another vehicle"
                required
                className="sm:col-span-6"
                {...form.getInputProps(`accidents.${index}.description`)}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <ApplicationRadioQuestion
                required
                question="Were you driving a commercial vehicle?"
                value={form.values.accidents[
                  index
                ].isCommercialMotorVehicleInvolved.toString()}
                handleChange={(value) =>
                  form.setFieldValue(
                    `accidents.${index}.isCommercialMotorVehicleInvolved`,
                    value === 'true'
                  )
                }
              />

              <ApplicationRadioQuestion
                required
                question="Did the event involve any fatalities?"
                value={form.values.accidents[
                  index
                ].involvedFatalities.toString()}
                handleChange={(value) =>
                  form.setFieldValue(
                    `accidents.${index}.involvedFatalities`,
                    value === 'true'
                  )
                }
              />

              <ApplicationRadioQuestion
                required
                question="Did the event involve any personal injuries?"
                value={form.values.accidents[index].involvedInjuries.toString()}
                handleChange={(value) =>
                  form.setFieldValue(
                    `accidents.${index}.involvedInjuries`,
                    value === 'true'
                  )
                }
              />

              <ApplicationRadioQuestion
                required
                question="Was the event preventable?"
                value={form.values.accidents[index].isPreventable.toString()}
                handleChange={(value) =>
                  form.setFieldValue(
                    `accidents.${index}.isPreventable`,
                    value === 'true'
                  )
                }
              />
            </div>
          </ApplicationAccordionItem>
        ))}
      </Accordion>

      {form.values.accidents.length > 0 && (
        <div className={`col-span-3 grid-cols-subgrid`}>
          <MantineButton
            size="sm"
            variant="light"
            classNames={{
              root: 'h-11 rounded-lg border border-doubleNickel-brand-500 bg-doubleNickel-brand-25 text-sm font-semibold text-doubleNickel-brand-700 hover:bg-doubleNickel-brand-50',
            }}
            onClick={() => {
              setAccordionValue(`accident-${form.values.accidents.length + 1}`)
              const key = uuid()
              const accident = {
                ...defaultAccident,
                key,
              }
              form.setFieldValue('accidents', [
                ...form.values.accidents,
                accident,
              ])
            }}
          >
            Add Event
          </MantineButton>
        </div>
      )}
    </ApplicationScreenWrapper>
  )
}
